import {
  editorStateToHtml,
  htmlToEditorState,
} from "@rentiohq/web-shared/dist/components/RichTextInput/richText.utils";

/**
 * A text as the editor would write it, with every difference a reader cannot
 * see taken out.
 *
 * A box holds two kinds of value: the plain-text default it was opened or
 * reset with, and the formatted HTML the editor hands back once the broker
 * types. Both go through the editor's own round trip, so the default compares
 * as the HTML it would be saved as, and formatting the broker added still
 * counts. Whitespace does not: not around tags, not doubled, and not in blank
 * lines at the end, which the editor keeps as empty paragraphs.
 */
const toComparableText = (value: string) =>
  editorStateToHtml(htmlToEditorState(value))
    .replace(/&nbsp;/g, " ")
    .replace(/(\s*<p><br><\/p>)+\s*$/, "")
    .replace(/\s+/g, " ")
    .replace(/\s*(<[^>]+>)\s*/g, "$1")
    .trim();

export const isSameText = (params: {
  left: string | null | undefined;
  right: string | null | undefined;
}) =>
  toComparableText(params.left || "") === toComparableText(params.right || "");

/**
 * What one save writes, per box.
 *
 * A box whose text equals what it would show with nothing stored is cleared
 * rather than written (DEV-3343). Writing it would freeze today's default as
 * the office's own text, and that office would never receive a later change
 * to the default. Every box is decided on its own, so editing one language
 * no longer freezes the other two.
 *
 * `null` is what removes a stored broker preference: the legacy preference
 * store deletes the key on it, where `undefined` would not even reach the
 * request.
 */
export const getPreferencesToSave = (params: {
  values: { key: string; value?: unknown }[];
  /**
   * Per key, the text the box falls back to: its default, or for a box with
   * a `fallbackKey` the text it inherits.
   */
  textWhenClearedByKey: { [key: string]: string };
}) => {
  const { values, textWhenClearedByKey } = params;

  const result: { [key: string]: string | null } = {};

  values.forEach(({ key, value }) => {
    const text = `${value || ""}`;
    const textWhenCleared = textWhenClearedByKey[key];

    result[key] =
      textWhenCleared !== undefined &&
      isSameText({ left: text, right: textWhenCleared })
        ? null
        : text;
  });

  return result;
};
